'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  applyButtonStyles,
  footerStyles,
  panelTokens,
} from './BulkTypeaheadSearch.styles'
import { CheckboxOption } from './TypeaheadSearch'
import { TypeaheadSearch } from './TypeaheadSearch'
import { primaryActionHeaderStyles } from './TypeaheadSearch.styles'

export type BulkTypeaheadSearchProps = {
  intentLabel: string
  options: CheckboxOption[]
  onToggle: (value: string) => void
  onQueryChange?: (q: string) => void
  onDismiss?: () => void
  onApply?: () => void
  noMatchLabel: string
  placeholder: string
  resultsHeader: string
  colorScheme?: ColorScheme
  confirmLabel?: string
  hasChanges?: boolean
  /** When true, disables the confirm button to indicate an in-flight mutation. */
  isSubmitting?: boolean
  /** Badge text shown next to an option's label when it is disabled. Defaults to `'Disabled'`. */
  disabledBadgeLabel?: string
}

export const BulkTypeaheadSearch = ({
  intentLabel,
  options,
  onToggle,
  onQueryChange,
  onDismiss,
  onApply,
  noMatchLabel,
  placeholder,
  resultsHeader,
  colorScheme = 'dark',
  confirmLabel,
  hasChanges,
  isSubmitting = false,
  disabledBadgeLabel,
}: BulkTypeaheadSearchProps) => {
  const pt = panelTokens[colorScheme]

  return (
    <Box
      bg={pt.containerBg}
      {...(pt.border !== undefined && {
        borderTop: pt.border,
        borderBottom: pt.border,
        borderColor: pt.containerBorder,
      })}
      borderRadius='0px'
      bottom='100%'
      height={pt.height}
      mb='4px'
      overflow='hidden'
      position='absolute'
      right={0}
      width='214px'
      zIndex={11}
    >
      <Box display='flex' flexDirection='column' height='100%' textAlign='left'>
        <Box
          {...primaryActionHeaderStyles(colorScheme)}
          {...(pt.dropdownBg !== undefined && {
            style: { backgroundColor: pt.dropdownBg },
          })}
        >
          {intentLabel}
        </Box>
        <TypeaheadSearch
          alwaysOpen
          autoFocus
          checkboxOptions={options}
          colorScheme={colorScheme}
          disabledBadgeLabel={disabledBadgeLabel}
          dropdownBg={pt.dropdownBg}
          fillHeight
          flatDropdown
          noMatchLabel={noMatchLabel}
          onApply={onApply}
          onDismiss={onDismiss}
          onQueryChange={onQueryChange}
          onToggle={onToggle}
          options={[]}
          placeholder={placeholder}
          resultsHeader={resultsHeader}
          showIcon={false}
          showResultsWhenEmpty
        />
        {confirmLabel && hasChanges && onApply && (
          <Box
            {...footerStyles(colorScheme)}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Box
              as='button'
              {...applyButtonStyles(isSubmitting, colorScheme)}
              aria-disabled={isSubmitting}
              onClick={onApply}
            >
              {confirmLabel}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
